import { useMemo } from "react"

import { useQueryParams } from "@/hooks/useQueryParams"

import { NOTEBOOK_BUILDER_PATH } from "../utils/generatedNotebooks"
import { createReportRequest } from "../utils/reportClient"
import type { Notebook } from "../utils/reportTypes"

export interface ReportEnvironment {
  request: ReturnType<typeof createReportRequest>
  notebookUrl: (entry: Notebook, view: boolean) => string
  updateLocation: (entry?: Notebook, revision?: number) => void
  sharingEnabled: boolean
}

/** Keep the authenticated app boundary separate from isolated component previews. */
export function useReportEnvironment(
  organizationSlug: string,
  workspaceId: string,
  override?: ReportEnvironment,
): ReportEnvironment {
  const queryParams = useQueryParams()
  const request = useMemo(
    () =>
      override?.request ?? createReportRequest(organizationSlug, workspaceId),
    [organizationSlug, workspaceId, override?.request],
  )
  return (
    override ?? {
      request,
      sharingEnabled: true,
      notebookUrl: (entry, view) => {
        const params = new URLSearchParams({ notebook: entry.id })
        if (view) {
          params.set("view", "1")
          params.set("revision", String(entry.revision))
        }
        return new URL(
          queryParams.getHref(
            `${NOTEBOOK_BUILDER_PATH}?${params}`,
            organizationSlug,
            workspaceId,
          ),
          window.location.origin,
        ).toString()
      },
      updateLocation: (entry, revision) => {
        queryParams.setMultiple(
          {
            notebook: entry?.id ?? null,
            revision: revision ? String(revision) : null,
            view: null,
          },
          { history: "replace", shallow: true },
        )
      },
    }
  )
}
